import { Theme } from '@mui/material/styles'
import { makeAutoObservable } from 'mobx'
import { createGapTheme, IThemeVariables } from 'theme/theme'
import { themeConfig } from '@gap/shared/theme'
import { setModalTheme } from 'reusable/mui/modals/modal-theme'
import themeCache from 'cache/themeCache'
import { prefersDarkMode, prefersReducedMotion } from 'reusable/utils/utils'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import { getContrastText } from 'reusable/mui-utils/mui-colors'
import { grey } from '@mui/material/colors'
import './fonts.css'

export type ThemeMode = 'dark' | 'light' | 'os-defined'

/**
 * Theme store managing light/dark mode.
 * 
 * Dark mode is a dev-only feature. Non-developers should always use light mode.
 * The theme toggle UI is hidden from non-developers (see ProfileMenu.tsx).
 * Default is 'light' mode for all users.
 */
export class ThemeStore {
   theme!: Theme
   backgroundImageUrl = '' as string | null
   private _themeVariables: IThemeVariables = {
      themeMode: themeCache.get() || 'light',
      pdfFormat: false,
      genericOpacity: 1000
   }

   constructor() {
      makeAutoObservable(this)
      this.setTheme()
      this.backgroundImageUrl = localStorage.getItem('new-background-image')
   }

   get themeMode() {
      return this._themeVariables.themeMode === 'os-defined'
         ? prefersDarkMode()
            ? 'dark'
            : 'light'
         : this._themeVariables.themeMode
   }

   get dark() {
      return this.theme.palette.mode === 'dark'
   }

   /** Whether animations should be skipped, as the operating system's reduce-motion setting asks. */
   get reducedMotion() {
      return prefersReducedMotion()
   }

   get themeVariables() {
      return this._themeVariables
   }

   get primaryContrastTextColor() {
      return getContrastText(themeConfig.primary['800'])
   }

   get lineColor() {
      return this.dark ? grey[300] : grey[600]
   }

   get lineLightColor() {
      return this.dark ? grey[400] : grey[500]
   }

   get backgroundImage() {
      return this.backgroundImageUrl
   }

   get backgroundColor() {
      const isDark = this.themeMode === 'dark'
      if (isDark) return '#1a1a1a'
      return themeConfig.light.background.alt
   }

   /**
    * Panel color palette for ThreePanelLayout based on current theme mode
    */
   get panelColors() {
      const isDark = this.themeMode === 'dark'
      return {
         background: isDark ? '#1a1a1a' : themeConfig.light.background.alt,
         panelBackground: isDark ? '#252525' : '#ffffff',
         panelBorder: isDark ? '#3a3a3a' : themeConfig.primary[200],
         headerBorder: isDark ? '#333333' : themeConfig.primary[200],
         text: isDark ? '#e8e8e8' : themeConfig.primary[900],
         textSecondary: isDark ? '#666666' : themeConfig.primary[400],
         iconColor: isDark ? '#888888' : themeConfig.primary[500],
         iconHover: isDark ? '#333333' : themeConfig.primary[100],
         scrollbarThumb: isDark ? '#444444' : themeConfig.primary[300],
         scrollbarThumbHover: isDark ? themeConfig.grey[600] : themeConfig.primary[400],
         buttonText: isDark ? '#888888' : themeConfig.primary[600],
         modalFormBackground: isDark ? '#252525' : '#ffffff',
         skeleton: isDark ? grey[700] : grey[300],
         button: {
            border: isDark ? themeConfig.primary[800] : themeConfig.primary[800],
            background: isDark ? themeConfig.primary[800] : themeConfig.primary[200],
            backgroundHover: isDark ? themeConfig.primary[900] : themeConfig.primary[300],
            color: isDark ? '#888888' : themeConfig.primary[800],
            hoverColor: isDark ? '#ffffff' : themeConfig.primary[700],
         }
      }
   }

   get questionBGColor() {
      return {
         fill: this.dark ? themeConfig.primary[800] : themeConfig.primary[200],
         text: this.dark ? themeConfig.primary[100] : themeConfig.primary[900],
      }
   }

   setBackgroundImage = (url?: string) => {
      if (url) {
         this.backgroundImageUrl = url
         localStorage.setItem('new-background-image', url)
      } else {
         this.backgroundImageUrl = null
         localStorage.removeItem('new-background-image')
      }
   }

   getThemeVariables = <T extends keyof IThemeVariables>(propertyName: T) => {
      return this._themeVariables[propertyName]
   }

   setThemeVariables = <T extends keyof IThemeVariables>(params: {
      propertyName: T
      newValue: IThemeVariables[T]
   }) => {
      this._themeVariables[params.propertyName] = params.newValue
      this.setTheme()

      // Reload to re-apply themeConfig.primary palette (inverted at startup for dark mode)
      if (params.propertyName === 'themeMode') {
         window.location.reload()
      }
   }

   /** Reset to light mode without triggering a page reload. Used to enforce light mode for non-developers. */
   resetToLightMode = () => {
      this._themeVariables.themeMode = 'light'
      this.setTheme()
   }

   toggleTheme = () => {
      this.setThemeVariables({
         propertyName: 'themeMode',
         newValue:
            this._themeVariables.themeMode === 'os-defined'
               ? 'dark'
               : this._themeVariables.themeMode === 'dark'
                  ? 'light'
                  : 'os-defined',
      })
   }

   private setTheme = () => {
      this.theme = createGapTheme(this._themeVariables)
      setModalTheme(this.theme)
      themeCache.set(this._themeVariables.themeMode)
   }
}

export const { Provider: ThemeStoreProvider, useProvidedStore: useThemeStore } =
   createStoreContext(ThemeStore)
